import React from "react";
import moment from "moment";
import RequireData from "hooks/RequireData";
import { fetchFile, fetchFileInfo } from "actions/file";
import { ListGroup } from "react-bootstrap";
import { colorClassForJob, statusTextForJob } from "utils/jobStatus";
import fileSelector from "selectors/fileSelector";
import fileStatusSelector from "selectors/fileStatusSelector";
import JobDetail2InfoResultsRow from "./JobDetail2InfoResultsRow";
import { useDispatch } from "react-redux";
import { fetchJob } from "actions/jobs";
import { Link } from "react-router-dom";
import fileInfoSelector from "selectors/fileInfo";
import fileInfoStatusSelector from "selectors/fileInfoStatus";
import SageErrorAlert from "kit/SageErrorAlert";

interface Props {
    job: sage.model.IJob;
}

const JobDetail2Info: React.FC<Props> = ({ job }) => {
    const dispatch = useDispatch();

    const [fileInfo, fileInfoStatus] = RequireData(
        fileInfoSelector(job.file_id || ""),
        fileInfoStatusSelector(job.file_id || ""),
        fetchFileInfo(job.file_id || "")
    );
    const [file, fileStatus] = RequireData(
        fileSelector(job.file_id || ""),
        fileStatusSelector(job.file_id || ""),
        fetchFile(job.file_id || "")
    );

    const createdText = React.useMemo(() => {
        const createdAt = new Date(job.created_at);
        return `Created ${moment(createdAt).fromNow()}`;
    }, [job]);

    const fileText = React.useMemo(() => {
        if (file) {
            return (
                <span>
                    Rendering with file{" "}
                    <Link to={`/home/files/create-job/${file.id}`}>
                        {file.friendly_name || file.original_name}
                    </Link>
                </span>
            );
        } else {
            return "Loading...";
        }
    }, [file]);

    const renderFramesText = React.useMemo(() => {
        if (!fileInfo) {
            return "Loading...";
        }
        if (job.render_animation) {
            return `Rendering animation from frames ${job.start_frame} to ${job.end_frame} using Blender ${fileInfo.blender_version} and ${job.render_engine}`;
        } else {
            return `Rendering image of frame ${job.start_frame} using Blender ${fileInfo.blender_version} and ${job.render_engine}`;
        }
    }, [job, fileInfo]);

    React.useEffect(() => {
        if (
            (job && job.job_state !== "FINISHED") ||
            job.job_state !== "CANCELED"
        ) {
            const interval = setInterval(() => {
                dispatch(fetchJob(job.id));
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [job, dispatch]);

    if (!job.file_id) {
        return null;
    }

    if (fileInfoStatus === "error" || fileStatus === "error") {
        return <SageErrorAlert />;
    }

    return (
        <div>
            <h5>Job Information</h5>
            <ListGroup>
                <ListGroup.Item variant={colorClassForJob(job)}>
                    {statusTextForJob(job)}
                </ListGroup.Item>
                <JobDetail2InfoResultsRow job={job} />
                <ListGroup.Item>{createdText}</ListGroup.Item>
                <ListGroup.Item>{fileText}</ListGroup.Item>
                <ListGroup.Item>{renderFramesText}</ListGroup.Item>
            </ListGroup>
        </div>
    );
};

export default JobDetail2Info;
